import {
  Injectable,
  OnDestroy,
  Injector,
  EventEmitter,
  Input,
  Inject
} from '@angular/core';
import {
  Unboxed,
  KeyValue,
  FormGroupState,
  AbstractControlState
} from 'ngrx-forms';
import {
  Observable,
  Subscription
} from 'rxjs';
import { AbstractFormDefinition } from './abstract-form-definition';
import { FormsSources } from './forms-sources';
import {
  Store,
  MemoizedSelector
} from '@ngrx/store';
import { DxFormGroup } from './form-group';
import { SetInputMapEntry } from './decorators/controls/set-input';
import { OnEventMapEntry } from './decorators/controls/on-event';
import {
  ON_EVENT_USER_DEFINED_PROPERTY,
  SET_INPUT_USER_DEFINED_PROPERTY
} from './decorators/controls/user-defined-property-keys';
import { NGXLogger } from 'ngx-logger';
import {
  map,
  filter,
  first,
  tap
} from 'rxjs/operators';
import { BASE_GROUP_STATE_SELECTOR } from './tokens';

export interface OnGroupFormSet {
  dxOnGroupFormSet(): void;
}

export const dxOnGroupFormSet = 'dxOnGroupFormSet';

@Injectable()
export class AbstractGroupComponent<
  FormValue extends KeyValue,
  FormGroup extends DxFormGroup<FormValue, any, any> = DxFormGroup<FormValue, any, any>,
  FormDefinition extends AbstractFormDefinition<
    any
  > = AbstractFormDefinition<any>,
  Value extends Unboxed<FormValue> = Unboxed<FormValue>
> implements OnDestroy {

  public get group(): FormGroupState<any> {
    return this._group;
  }

  @Input() public set group(value: FormGroupState<any>) {
    this._group = value;
    if (!this.ngrxFormGroup) {
      if (this.controlPath) {
        this.ngrxFormGroup = this.applyControlPath(this._group, this.controlPath)
      }
    }
  }

  private _group: FormGroupState<any>;
  private _controlPath: string;

  public get controlPath(): string {
    return this._controlPath;
  }

  @Input() public set controlPath(controlPath: string) {
    this._controlPath = controlPath;
    if (!this.ngrxFormGroup) {
      if (this.group) {
        this.ngrxFormGroup = this.applyControlPath(this.group, controlPath);
      } else if (this.baseSelector) {
        this.store$.select(this.baseSelector)
            .pipe(
              filter(Boolean),
              first(),
              map(baseState => this.applyControlPath(baseState, controlPath)),
              tap(state => this.ngrxFormGroup = state),
            ).subscribe()

      } else {
        this.logger.warn('[AbstractFormArrayComponent::(set)controlPath] baseSelector is not defined');
      }
    }
  }

  public get dxFormGroup(): FormGroup {
    return this._dxFormGroup;
  }

  public set dxFormGroup(value: FormGroup) {
    this._dxFormGroup = value;
    this.value$ = this._dxFormGroup.__value$ as any;
    this.state$ = this._dxFormGroup.__state$ as any;
    this.controls$ = this.state$.pipe(map(state => state.controls)) as any;
    if (
      this[dxOnGroupFormSet] &&
      typeof this[dxOnGroupFormSet] === 'function'
    ) {
      this[dxOnGroupFormSet]();
    }
  }

  @Input()
  public set ngrxFormGroup(value: FormGroupState<FormValue>) {
    this.state = value;
    const formId = value.id.match(/^[^\.]+/)[0];
    if (!this.formDefinition || this.formDefinition.formId !== formId) {
      const controlPath = value.id
        .match(/^[^\.]+\.(.*)$/)[1]
        .split('.')
        .join('/');
      this.formDefinition = this.formsSources.getForm(formId);
      this.dxFormGroup = this.formDefinition.getControl(controlPath);
    }
    this.processUserDefinedProperties(value.userDefinedProperties);
  }

  public get valid(): boolean {
    return Object.keys(this.state.errors || {}).length === 0;
  }

  public get invalid(): boolean {
    return !this.valid;
  }

  public get value(): Value {
    return this._dxFormGroup && (this._dxFormGroup.__value as any);
  }

  public set value(value: Value) {
    if (this._dxFormGroup) {
      this._dxFormGroup.setValue(value);
    }
  }

  public value$: Observable<Value>;
  public state$: Observable<FormGroupState<FormValue>>;
  public controls$: Observable<
    {
      [controlId in keyof FormValue]: AbstractControlState<
        FormValue[keyof FormValue]
      >
    }
  >;

  private _dxFormGroup: FormGroup;
  public formDefinition: FormDefinition;
  public state: FormGroupState<FormValue>;
  protected _subscription = new Subscription();
  private _onEventSubscriptions: string[] = [];

  public readonly baseSelector: MemoizedSelector<any, FormGroupState<any>>;

  public constructor(
    public injector: Injector,
    public formsSources: FormsSources,
    public store$: Store<any>,
    public logger: NGXLogger,
    @Inject(BASE_GROUP_STATE_SELECTOR) baseSelector: any,
  ) {
    this.baseSelector = baseSelector;
  }

  private applyControlPath(
    baseState: FormGroupState<any>,
    controlPath: string
  ): FormGroupState<any> {
    const targetState: FormGroupState<any> = controlPath.split('/').reduce((state, controlId) => {
      if (state.controls.hasOwnProperty(controlId)) {
        return state.controls[controlId];
      }
      this.logger.error(
        `[AbstractGroupComponent::ngOnInit::${controlPath}] control path is not valid`,
        { dxFormControl: controlPath, baseState }
      );
      throw new Error(
        `[AbstractGroupComponent::ngOnInit::${controlPath}] control path is not valid`
      );
    }, baseState) as any;

    if (!targetState.controls || Array.isArray(targetState.controls)) {
      throw new Error(
        `[AbstractGroupComponent::ngOnInit::${controlPath}] targetState is not an FormGroupState`
      );
    }

    return targetState as FormGroupState<any>;
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this._subscription = new Subscription();
    this._onEventSubscriptions = [];
  }

  public processUserDefinedProperties(userDefinedProperties: KeyValue): void {
    if (userDefinedProperties.hasOwnProperty(ON_EVENT_USER_DEFINED_PROPERTY)) {
      for (const onEvent of userDefinedProperties[
        ON_EVENT_USER_DEFINED_PROPERTY
      ] as Array<OnEventMapEntry>) {
        if (
          this.hasOwnProperty(onEvent.output) &&
          this[onEvent.output] instanceof EventEmitter
        ) {
          if (
            !this._onEventSubscriptions.find(
              output => output === onEvent.output
            )
          ) {
            this._subscription.add(
              (<EventEmitter<any>>this[onEvent.output]).subscribe($event =>
                onEvent.handler($event)
              )
            );
            this._onEventSubscriptions.push(onEvent.output);
          }
        } else {
          console.warn(
            `Defined output '${onEvent.output}' is not found on control '${
              Object.getPrototypeOf(this).constructor.name
            }'`
          );
        }
      }
    }
    if (userDefinedProperties.hasOwnProperty(SET_INPUT_USER_DEFINED_PROPERTY)) {
      for (const setInput of userDefinedProperties[
        SET_INPUT_USER_DEFINED_PROPERTY
      ] as Array<SetInputMapEntry>) {
        if (this.hasOwnProperty(setInput.input)) {
          this[setInput.input] = setInput.value;
        } else {
          console.warn(
            `Defined input '${setInput.input}' is not found on control '${
              Object.getPrototypeOf(this).constructor.name
            }'`
          );
        }
      }
    }
  }
}
